import { Component, input } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { RouterModule } from '@angular/router';
import { TranslocoModule } from '@jsverse/transloco';
import type { RailBarRouteItemType } from '../..';

@Component({
  selector: 'dtis-rail-btn',
  host: { class: 'block max-w-full w-full' },
  styles: [
    `
      @reference "tailwindcss";
      :host ::ng-deep .rail-btn-expanded.mat-mdc-button {
        justify-content: flex-start;
        --mat-button-text-state-layer-color: var(--on-surface-color, #343434);
      }

      :host ::ng-deep .rail-btn-expanded.mat-mdc-button.rail-nav-item-active {
        background-color: var(--secondary-container-color, #d2e0db);
      }

      :host ::ng-deep .rail-btn-collapsed.mat-mdc-icon-button.rail-nav-item-active {
        background-color: var(--secondary-container-color, #d2e0db);
      }

      :host ::ng-deep .rail-btn-collapsed.mat-mdc-icon-button:hover:not(.rail-nav-item-active) {
        background-color: rgba(52, 52, 52, 0.08);
      }

      :host ::ng-deep .rail-btn-collapsed.mat-mdc-icon-button.rail-nav-item-active:hover {
        background-image: linear-gradient(rgba(0, 41, 25, 0.12), rgba(0, 41, 25, 0.12));
      }

      :host ::ng-deep .rail-btn-collapsed.mat-mdc-icon-button:focus-visible {
        outline: 2px solid var(--primary-color, #006b52);
        outline-offset: 2px;
      }

      ::ng-deep .mdc-button__label {
        @apply max-w-full overflow-hidden text-ellipsis text-nowrap;
      }
    `,
  ],
  imports: [RouterModule, MatButtonModule, MatIconModule, TranslocoModule],
  template: `    
    <div
      class="rail-btn-grp flex w-full grow-0 items-center gap-1"
      [class.flex-col]="!expanded$()"
    >
      @if (expanded$()) {
      <button
        [routerLink]="route$()"
        [routerLinkActive]="['rail-nav-item-active']"
        [routerLinkActiveOptions]="{ exact: exactMatch$() }"
        [disabled]="item$().disabled"
        (click)="handleClick($event)"
        class="rail-btn-expanded flex !h-14 !w-full !min-w-0 items-center gap-3 !rounded-full !px-4 !pr-6"
        mat-button
      >
        <mat-icon
        [routerLinkActiveOptions]="{ exact: exactMatch$() }"
        [routerLinkActive]="['!text-onSecondaryContainer']"
        class="!h-6 !w-6 !text-[24px] !leading-6 shrink-0 text-onSurfaceVariant"
          >{{ item$().icon }}</mat-icon
        >
        <span
        [routerLinkActiveOptions]="{ exact: exactMatch$() }"
        [routerLinkActive]="['!text-onSecondaryContainer', '!font-semibold']"
          class="max-w-full flex-1 overflow-hidden text-ellipsis text-nowrap text-[14px] leading-5 tracking-[0.1px] text-onSurfaceVariant font-medium"
        >
          {{ label$() | transloco }}
        </span>
      </button>

      }@else {
      <a
        [routerLink]="route$()"
        [routerLinkActive]="['rail-nav-item-active']"
        [routerLinkActiveOptions]="{ exact: exactMatch$() }"
        [disabled]="item$().disabled"
        (click)="handleClick($event)"
        class="rail-btn-collapsed flex !h-8 !w-14 !min-w-14 items-center justify-center !rounded-full !px-4 !py-1"
        mat-icon-button
      >
        <mat-icon class="!h-6 !w-6 !text-[24px] !leading-6 text-onSurfaceVariant"
          [routerLinkActiveOptions]="{ exact: exactMatch$() }"
          [routerLinkActive]="['!text-onSecondaryContainer']"
          >{{ icon$() }}</mat-icon
        >
      </a>
      <span
        [routerLink]="route$()"
        [routerLinkActiveOptions]="{ exact: exactMatch$() }"
        [routerLinkActive]="['!text-onSurface', '!font-semibold']"
        class="text-onSurfaceVariant max-w-full overflow-hidden text-ellipsis text-nowrap text-center text-[12px] leading-4 tracking-[0.5px] font-medium"
        [title]="label$()"
      >
        {{ label$() | transloco }} 
      </span>

      }
    </div>
  `,
})
/**
 * Represents a button component within a slim navigation rail.
 */
export class RailButtonComponent {
  item$ = input.required<RailBarRouteItemType>();
  /**
   * Signal that emits the state of the sidebar expansion.
   * It selects the `isSidebarExpanded` property from the `SettingsState`.
   */
  expanded$ = input<boolean>(true);
  /**
   * An Signal that emits the current route.
   * Omit this and provide `onClick$` instead to use the button as an action trigger rather than a link.
   *
   * @type {Signal<string[]>}
   */
  route$ = input<string | string[] | undefined>(undefined);
  /**
   * Optional handler invoked on click instead of navigating. Takes precedence over `route$`.
   */
  onClick$ = input<(() => void) | undefined>(undefined);
  /**
   * Signal that holds the icon name for the rail button component.
   *
   * @type {Signal<string>}
   */
  icon$ = input('home');
  /**
   * Signal that holds the label for the rail button component.
   * The label is initialized with the value 'home'.
   */
  label$ = input('home');
  /**
   * Whether routerLinkActive should use exact matching.
   * Set to true for the home route to avoid it always being active.
   */
  exactMatch$ = input(false);

  /**
   * Invokes the optional click handler instead of letting the routerLink navigate.
   */
  handleClick(event: Event) {
    const onClick = this.onClick$();
    if (onClick) {
      event.preventDefault();
      onClick();
    }
  }
}
